import { Check, Clock, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { fmtDistance, fmtTime } from '../assistantFormat';
import type { AssistantObject, SuggestionReason } from '../assistantTypes';

interface Props {
  reasons: SuggestionReason[] | null | undefined;
  objects: Map<string, AssistantObject>;
  wrap?: boolean;
  /** The employee's `startPoint.source` ('Override' | 'Home' | 'FirstObject' | 'None'). */
  startSource?: string | null;
}

/** Reason chips built from the structured reasons of a fit (max. 4). */
export function ReasonChips({ reasons, objects, wrap = true, startSource }: Props) {
  const { t, i18n } = useTranslation('planning');
  const startUnknown = startSource === 'None';
  const items = (reasons ?? []).slice(0, 4).map((r) => {
    if (r.kind === 'Gap') {
      return { icon: Clock, text: t('assistant.reasons.gapFree', { from: fmtTime(r.gapFrom), to: fmtTime(r.gapTo) }), strong: r.strong };
    }
    if (r.kind === 'Visits') {
      return { icon: Check, text: t('assistant.reasons.visits', { count: r.visits ?? 0 }), strong: r.strong };
    }
    let text: string;
    if (r.onSite) {
      text = t('assistant.reasons.onSite');
    } else if (r.distanceKm == null) {
      // Measured from the start point (no neighbouring object) and that point is unknown: the
      // object may well have coordinates — say what is actually missing.
      const fromUnknownStart = !r.nearObjectId && !r.fromHome && startUnknown;
      text = t(fromUnknownStart ? 'assistant.reasons.startUnknown' : 'assistant.reasons.noCoordinates');
    } else if (!r.fromHome && !r.nearObjectId && startSource === 'Override') {
      // Measured from the planner's start point override for this day, not from home.
      text = t('assistant.reasons.distanceFromStart', { distance: fmtDistance(r.distanceKm, i18n.language) });
    } else if (r.fromHome || !r.nearObjectId) {
      text = t('assistant.reasons.distanceFromHome', { distance: fmtDistance(r.distanceKm, i18n.language) });
    } else {
      const near = objects.get(r.nearObjectId);
      text = t('assistant.reasons.distanceFrom', {
        distance: fmtDistance(r.distanceKm, i18n.language),
        object: near?.shortName || near?.name || '',
      });
    }
    return { icon: MapPin, text, strong: r.strong };
  });

  if (items.length === 0) return null;
  return (
    <div className={`flex gap-1 ${wrap ? 'flex-wrap' : 'overflow-hidden'}`}>
      {items.map(({ icon: Icon, text, strong }, i) => (
        <span
          key={i}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] whitespace-nowrap tabular-nums ${
            strong ? 'bg-emerald-50 text-emerald-800' : 'bg-slate-100 text-slate-600'
          }`}
        >
          <Icon size={11} strokeWidth={2} className="shrink-0" />
          {text}
        </span>
      ))}
    </div>
  );
}
